import React, { useCallback, useEffect, useState } from "react";
import { MenuItem, Select } from "@mui/material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import {
  EMPTY_PASSAGENS_FILTER_STATE,
  normalizePassagensFilterState,
} from "../../utils/dashboardPassagensFilterState";
import DashboardSavedFiltersPanel from "./DashboardSavedFiltersPanel";
import {
  DashboardFilterModalActions,
  DashboardFilterModalFrame,
  DashboardFilterSection,
  DashboardMultiPickList,
} from "./dashboardFilterModalUi";

function extractList(res) {
  const raw = res?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.items)) return raw.items;
  return [];
}

export default function DashboardPassagensFiltersModal({
  open,
  onClose,
  initialState = EMPTY_PASSAGENS_FILTER_STATE,
  onApply,
}) {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(false);
  const [origensOptions, setOrigensOptions] = useState([]);
  const [destinosOptions, setDestinosOptions] = useState([]);
  const [channels, setChannels] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [channelIds, setChannelIds] = useState([]);
  const [status, setStatus] = useState("");
  const prevOpen = React.useRef(false);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        fetchData("/dashboard/venda-passagens/filter-options", "GET"),
        fetchData("/channels?limit=200", "GET"),
      ]);
      const [geo, ch] = results.map((r) => (r.status === "fulfilled" ? r.value : null));
      const geoPayload = geo?.data ?? geo ?? {};
      setOrigensOptions(Array.isArray(geoPayload.origens) ? geoPayload.origens : []);
      setDestinosOptions(Array.isArray(geoPayload.destinos) ? geoPayload.destinos : []);
      setChannels(extractList(ch));
    } catch (e) {
      console.error("Erro ao carregar opções de filtros de passagens:", e);
      setOrigensOptions([]);
      setDestinosOptions([]);
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return;
    }
    const opening = !prevOpen.current;
    prevOpen.current = true;
    if (opening) {
      const s = normalizePassagensFilterState(initialState);
      setOrigens(s.origens);
      setDestinos(s.destinos);
      setChannelIds(s.ids_origens);
      setStatus(s.status);
      loadOptions();
    }
  }, [open, initialState, loadOptions]);

  const buildLabels = () => {
    const origensLabels = {};
    origensOptions.forEach((o) => {
      const id = String(o?.id || o?.nome || "").trim();
      if (!id) return;
      origensLabels[id] = String(o?.nome || id);
    });
    const destinosLabels = {};
    destinosOptions.forEach((d) => {
      const id = String(d?.id || d?.nome || "").trim();
      if (!id) return;
      destinosLabels[id] = String(d?.nome || id);
    });
    const channelLabels = {};
    channels.forEach((c) => {
      const id = Number(c?.id);
      if (!Number.isFinite(id)) return;
      channelLabels[String(id)] = String(c?.nome || c?.name || `#${id}`);
    });
    return { origens: origensLabels, destinos: destinosLabels, channels: channelLabels };
  };

  const handleClear = () => {
    setOrigens([]);
    setDestinos([]);
    setChannelIds([]);
    setStatus("");
  };

  const handleApply = () => {
    onApply?.(
      normalizePassagensFilterState({
        origens,
        destinos,
        ids_origens: channelIds,
        status,
        labels: buildLabels(),
      })
    );
    onClose?.();
  };

  const hasActive =
    origens.length > 0 || destinos.length > 0 || channelIds.length > 0 || Boolean(status);

  return (
    <DashboardFilterModalFrame
      open={open}
      onClose={onClose}
      icon={<ConfirmationNumberOutlinedIcon />}
      title={t("dashboardPage.passagensFilters.title", { defaultValue: "Filtrar passagens" })}
      subtitle={t("dashboardPage.passagensFilters.subtitle", {
        defaultValue: "Bilhetes e funil passam a considerar só este recorte",
      })}
      loading={loading}
      footer={
        <DashboardFilterModalActions
          onClear={handleClear}
          onClose={onClose}
          onApply={handleApply}
          hasActive={hasActive}
          clearLabel={t("dashboardPage.passagensFilters.clearAll", { defaultValue: "Limpar" })}
          cancelLabel={t("dashboardPage.passagensFilters.cancel", { defaultValue: "Cancelar" })}
          applyLabel={t("dashboardPage.passagensFilters.apply", { defaultValue: "Aplicar" })}
        />
      }
    >
      <DashboardFilterSection
        title={t("dashboardPage.passagensFilters.origens", { defaultValue: "Cidade de origem" })}
        selectedCount={origens.length}
      >
        <DashboardMultiPickList
          options={origensOptions}
          value={origens}
          onChange={setOrigens}
          getLabel={(o) => o?.nome || o?.id}
          getValue={(o) => o?.id || o?.nome}
          valueAsString
          searchPlaceholder={t("dashboardPage.passagensFilters.searchOrigens", {
            defaultValue: "Buscar origens…",
          })}
          emptyLabel={t("dashboardPage.passagensFilters.emptyOrigens", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.passagensFilters.destinos", { defaultValue: "Cidade de destino" })}
        selectedCount={destinos.length}
      >
        <DashboardMultiPickList
          options={destinosOptions}
          value={destinos}
          onChange={setDestinos}
          getLabel={(o) => o?.nome || o?.id}
          getValue={(o) => o?.id || o?.nome}
          valueAsString
          searchPlaceholder={t("dashboardPage.passagensFilters.searchDestinos", {
            defaultValue: "Buscar destinos…",
          })}
          emptyLabel={t("dashboardPage.passagensFilters.emptyDestinos", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.passagensFilters.channels", { defaultValue: "Canal de venda" })}
        selectedCount={channelIds.length}
      >
        <DashboardMultiPickList
          options={channels}
          value={channelIds}
          onChange={setChannelIds}
          getLabel={(o) => o?.nome || o?.name || `#${o?.id}`}
          getValue={(o) => o?.id}
          searchPlaceholder={t("dashboardPage.passagensFilters.searchChannels", {
            defaultValue: "Buscar canais…",
          })}
          emptyLabel={t("dashboardPage.passagensFilters.emptyChannels", {
            defaultValue: "Nada encontrado",
          })}
        />
      </DashboardFilterSection>

      <DashboardFilterSection
        title={t("dashboardPage.passagensFilters.status", { defaultValue: "Situação do bilhete" })}
      >
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">
            {t("dashboardPage.passagensFilters.statusAll", { defaultValue: "Qualquer" })}
          </MenuItem>
          <MenuItem value="active">
            {t("dashboardPage.passagensFilters.statusActive", { defaultValue: "Ativas" })}
          </MenuItem>
          <MenuItem value="cancelled">
            {t("dashboardPage.passagensFilters.statusCancelled", { defaultValue: "Canceladas" })}
          </MenuItem>
        </Select>
      </DashboardFilterSection>

      {!loading ? (
        <DashboardSavedFiltersPanel
          section="venda-passagens"
          getSnapshot={() =>
            normalizePassagensFilterState({
              origens,
              destinos,
              ids_origens: channelIds,
              status,
              labels: buildLabels(),
            })
          }
          onApply={(state) => {
            onApply?.(state);
            onClose?.();
          }}
          canSave
        />
      ) : null}
    </DashboardFilterModalFrame>
  );
}
